import { useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

import type { SavedView } from '../../MainSidebar/components/SavedViewsSection/hooks'
import type { CommandBarAction } from '../actions/types'

/**
 * Return value from the useCommandBarTracking hook.
 */
interface UseCommandBarTrackingResult {
  /** Track when the command bar is opened */
  trackOpen: (source: 'keyboard_shortcut' | 'button_click') => void
  /** Track when an action is selected */
  trackActionSelected: (
    action: CommandBarAction,
    searchQuery: string,
    resultPosition: number,
  ) => void
  /** Track when a saved view is activated from the command bar */
  trackSavedViewLoaded: (view: SavedView) => void
}

/**
 * Hook for tracking command bar interactions.
 *
 * Uses componentRendered event for now until specific command bar events
 * are added to the tracking plan.
 */
export function useCommandBarTracking(): UseCommandBarTrackingResult {
  const commonTrackingProperties = useCommonTrackingProperties()
  const organizationId = useOrganizationId()
  const location = useLocation()
  const openTimeRef = useRef<number>(0)

  const trackOpen = useCallback(
    (source: 'keyboard_shortcut' | 'button_click') => {
      openTimeRef.current = Date.now()

      if (!commonTrackingProperties) return

      BufferTracker.componentRendered({
        ...commonTrackingProperties,
        path: location.pathname,
        componentName: 'CommandBar',
        componentDetails: JSON.stringify({
          source,
          event: 'opened',
        }),
      })
    },
    [commonTrackingProperties, location.pathname],
  )

  const trackActionSelected = useCallback(
    (action: CommandBarAction, searchQuery: string, resultPosition: number) => {
      const timeToSelect = Date.now() - openTimeRef.current

      if (!commonTrackingProperties) return

      BufferTracker.componentRendered({
        ...commonTrackingProperties,
        path: location.pathname,
        componentName: 'CommandBar',
        componentDetails: JSON.stringify({
          event: 'action_selected',
          actionId: action.id,
          actionSection: action.section,
          searchQuery: searchQuery || null,
          resultPosition,
          timeToSelectMs: timeToSelect,
        }),
      })
    },
    [commonTrackingProperties, location.pathname],
  )

  const trackSavedViewLoaded = useCallback(
    (view: SavedView) => {
      BufferTracker.savedViewLoaded({
        ...commonTrackingProperties,
        clientName: 'publishWeb',
        organizationId: organizationId ?? '',
        viewId: view.id,
        section: view.section,
        view: view.view,
        source: 'commandK',
      })
    },
    [commonTrackingProperties, organizationId],
  )

  return {
    trackOpen,
    trackActionSelected,
    trackSavedViewLoaded,
  }
}
